import type { FC } from "react";

type SemaforoNivel = "verde" | "amarillo" | "rojo";

interface CriterioResultado {
  criterio: string;
  cumple: boolean;
  observacion?: string;
}

interface Props {
  nivel: SemaforoNivel;
  criterios: CriterioResultado[];
  className?: string;
  compact?: boolean;
}

const NIVEL_CONFIG: Record<SemaforoNivel, { label: string; desc: string; bg: string; border: string; text: string; icon: string; ring: string }> = {
  verde: {
    label: "Informe claro, objetivo y suficiente",
    desc: "El informe cumple con los criterios de calidad. Está listo para ser enviado.",
    bg: "bg-emerald-50/80",
    border: "border-emerald-200/70",
    text: "text-emerald-700",
    icon: "ri-check-double-line text-emerald-500",
    ring: "ring-emerald-200/50",
  },
  amarillo: {
    label: "Informe requiere mayor precisión",
    desc: "Faltan antecedentes o hay secciones que necesitan más detalles. Revise las sugerencias antes de enviar.",
    bg: "bg-amber-50/80",
    border: "border-amber-200/70",
    text: "text-amber-700",
    icon: "ri-alert-line text-amber-500",
    ring: "ring-amber-200/50",
  },
  rojo: {
    label: "Informe contiene lenguaje subjetivo o información insuficiente",
    desc: "Se detectaron frases con juicios de valor, diagnósticos no respaldados o secciones con información insuficiente. Debe corregir antes de enviar.",
    bg: "bg-red-50/80",
    border: "border-red-200/70",
    text: "text-red-700",
    icon: "ri-close-circle-line text-red-500",
    ring: "ring-red-200/50",
  },
};

const ReportQualitySemaphore: FC<Props> = ({ nivel, criterios, className = "", compact = false }) => {
  const config = NIVEL_CONFIG[nivel];
  const aprobados = criterios.filter((c) => c.cumple).length;
  const total = criterios.length;
  const porcentaje = Math.round((aprobados / total) * 100);

  return (
    <div className={`rounded-2xl border ${config.border} ${config.bg} overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-background-200/30">
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-10 h-10 rounded-xl bg-white ${config.ring} ring-2 flex-shrink-0`}>
            <i className={`${config.icon} text-lg`} />
          </span>
          <div className="min-w-0">
            <p className={`text-sm font-bold ${config.text}`}>{config.label}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{config.desc}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className={`text-2xl font-bold ${config.text}`}>{porcentaje}%</p>
            <p className="text-[11px] text-foreground-400">{aprobados}/{total} criterios</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 rounded-full bg-background-200/70 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out-expo ${
              nivel === "verde" ? "bg-emerald-500" : nivel === "amarillo" ? "bg-amber-500" : "bg-red-500"
            }`}
            style={{ width: `${porcentaje}%` }}
          />
        </div>
      </div>

      {/* Criterios */}
      {!compact && (
        <div className="px-5 py-3 space-y-2">
          {criterios.map((criterio, idx) => (
            <div key={idx} className="flex items-start gap-2.5 py-1.5">
              <span className={`flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 mt-0.5 ${
                criterio.cumple ? "bg-emerald-100" : "bg-red-100"
              }`}>
                <i className={`${criterio.cumple ? "ri-check-line text-emerald-500" : "ri-close-line text-red-500"} text-xs`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-medium ${criterio.cumple ? "text-foreground-700" : "text-red-700"}`}>
                  {criterio.criterio}
                </p>
                {criterio.observacion && (
                  <p className="text-[11px] text-foreground-400 mt-0.5">{criterio.observacion}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportQualitySemaphore;
export type { SemaforoNivel, CriterioResultado };